import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Dumbbell, Clock, ChevronRight, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { loadAllDrills, type TrainingDrill } from '@/lib/training-program/drill-loader';

type DrillCategory = 'full_swing' | 'putting' | 'short_game' | 'bunker' | 'all';

const CATEGORY_LABELS: Record<DrillCategory, string> = {
  all: 'All Drills',
  full_swing: 'Full Swing',
  putting: 'Putting',
  short_game: 'Short Game',
  bunker: 'Bunker',
};

const DIFFICULTY_COLORS: Record<string, string> = {
  beginner: 'bg-green-100 text-green-700',
  intermediate: 'bg-yellow-100 text-yellow-700',
  advanced: 'bg-orange-100 text-orange-700',
};

export default function DrillsLibraryPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [drills, setDrills] = useState<TrainingDrill[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<DrillCategory>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const allDrills = await loadAllDrills();
        if (!cancelled) {
          setDrills(allDrills);
        }
      } catch (error) {
        console.error('Failed to load drills:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const filteredDrills = useMemo(() => {
    let result = drills;

    // Filter by category
    if (selectedCategory !== 'all') {
      result = result.filter((drill) => drill.category === selectedCategory);
    }

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (drill) =>
          drill.title.toLowerCase().includes(query) ||
          drill.description?.toLowerCase().includes(query) ||
          drill.mvp_drill_objective?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [drills, selectedCategory, searchQuery]);

  const categoryCounts = useMemo(() => {
    const counts: Record<DrillCategory, number> = {
      all: drills.length,
      full_swing: 0,
      putting: 0,
      short_game: 0,
      bunker: 0,
    };

    drills.forEach((drill) => {
      const cat = drill.category as DrillCategory;
      if (counts[cat] !== undefined) {
        counts[cat]++;
      }
    });

    return counts;
  }, [drills]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0D382A] to-[#196644] px-4 py-6">
        <h1 className="text-white text-2xl font-semibold mb-2">Drill Library</h1>
        <p className="text-white/80 text-sm">Browse all available drills</p>
      </div>

      {/* Search */}
      <div className="px-4 py-3 bg-white border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search drills..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Category Tabs */}
      <div className="px-4 py-3 bg-white border-b overflow-x-auto">
        <div className="flex gap-2 min-w-max">
          {(Object.keys(CATEGORY_LABELS) as DrillCategory[]).map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                selectedCategory === cat
                  ? 'bg-[#196644] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {CATEGORY_LABELS[cat]} ({categoryCounts[cat]})
            </button>
          ))}
        </div>
      </div>

      {/* Drills List */}
      <div className="px-4 py-4 space-y-3">
        {filteredDrills.length === 0 ? (
          <div className="text-center py-12">
            <Dumbbell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No drills found</p>
          </div>
        ) : (
          filteredDrills.map((drill) => (
            <button
              key={drill.slug}
              onClick={() => navigate(`/drills/${drill.slug}`)}
              className="w-full bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-left hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 mb-1">{drill.title}</h3>
                  <p className="text-sm text-gray-500 line-clamp-2 mb-2">
                    {drill.mvp_drill_objective || drill.description}
                  </p>
                  <div className="flex items-center gap-2">
                    {drill.duration_minutes && (
                      <span className="flex items-center gap-1 text-xs text-gray-400">
                        <Clock className="w-3 h-3" />
                        {drill.duration_minutes} min
                      </span>
                    )}
                    {drill.difficulty && (
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                          DIFFICULTY_COLORS[drill.difficulty] || 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {drill.difficulty}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
